<template>
  <v-container fluid>
    <!-- Botón volver -->
    <v-btn text class="mb-3" :to="{ name: 'asignacion-beneficiarios-listado' }">
      <v-icon left>mdi-arrow-left</v-icon>
      Volver al listado
    </v-btn>

    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title class="d-flex align-center">
            <v-icon left class="mr-2">mdi-account-multiple-plus</v-icon>
            Asignación Masiva de Cupos
          </v-card-title>

          <v-card-text>
            <v-alert type="info" text dense class="mb-4">
              Seleccione las postulaciones validadas y asigne un estado (Beneficiario o Lista de Espera).
            </v-alert>

            <!-- Filtros -->
            <v-row class="mb-3">
              <v-col cols="12" md="4">
                <v-autocomplete
                  v-model="filtroMunicipalidad"
                  :items="municipalidades"
                  item-text="nombre"
                  item-value="id"
                  label="Filtrar por Municipalidad"
                  clearable
                  outlined
                  dense
                  hide-details
                  @change="cargarPostulaciones"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="filtroRut"
                  label="Buscar por RUT"
                  clearable
                  outlined
                  dense
                  hide-details
                  prepend-inner-icon="mdi-magnify"
                  @keyup.enter="cargarPostulaciones"
                  @click:clear="filtroRut = null; cargarPostulaciones()"
                />
              </v-col>
            </v-row>

            <!-- Acción masiva -->
            <v-row v-if="seleccionadas.length > 0" class="mb-3">
              <v-col cols="12" class="d-flex align-center">
                <v-chip color="primary" class="mr-3">
                  {{ seleccionadas.length }} seleccionada(s)
                </v-chip>
                <v-select
                  v-model="estadoAsignar"
                  :items="estadosAsignar"
                  label="Asignar como"
                  outlined
                  dense
                  hide-details
                  style="max-width: 250px;"
                  class="mr-3"
                />
                <v-btn
                  color="primary"
                  :loading="procesando"
                  :disabled="!estadoAsignar"
                  @click="dialogConfirmar = true"
                >
                  <v-icon left>mdi-check-all</v-icon>
                  Asignar cupos
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>

          <v-divider />

          <!-- Tabla de postulaciones validadas -->
          <v-data-table
            v-model="seleccionadas"
            :headers="headers"
            :items="postulaciones"
            :loading="cargando"
            :server-items-length="totalPostulaciones"
            :options.sync="opciones"
            :footer-props="{
              'items-per-page-options': [15, 30, 50],
            }"
            show-select
            item-key="id"
            class="elevation-0"
            @update:options="cargarPostulaciones"
          >
            <!-- Nombre -->
            <template #[`item.nombre_completo`]="{ item }">
              {{ item.nombre_completo || getNombreCompleto(item) }}
            </template>

            <!-- Sector -->
            <template #[`item.sector`]="{ item }">
              <v-chip x-small :color="item.sector === 'cementerio' ? 'deep-purple lighten-4' : 'blue lighten-4'">
                {{ item.sector === 'cementerio' ? 'Cementerio' : 'Municipal' }}
              </v-chip>
            </template>

            <!-- Municipalidad -->
            <template #[`item.municipalidad`]="{ item }">
              {{ item.certificado ? item.certificado.municipalidad_id : '-' }}
            </template>

            <!-- Fecha -->
            <template #[`item.updated_at`]="{ item }">
              {{ formatearFecha(item.updated_at) }}
            </template>

            <!-- Sin datos -->
            <template #no-data>
              <div class="text-center py-5">
                <v-icon size="64" color="grey lighten-2">
                  mdi-account-check-outline
                </v-icon>
                <p class="text-h6 grey--text mt-3">
                  No hay postulaciones validadas pendientes de asignación
                </p>
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Diálogo de confirmación -->
    <v-dialog v-model="dialogConfirmar" max-width="500">
      <v-card>
        <v-card-title class="text-h5">
          Confirmar asignación masiva
        </v-card-title>
        <v-card-text>
          ¿Está seguro que desea asignar
          <strong>{{ seleccionadas.length }}</strong> postulación(es) como
          <strong>{{ estadoAsignar === 'beneficiario' ? 'Beneficiario' : 'Lista de Espera' }}</strong>?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogConfirmar = false">
            Cancelar
          </v-btn>
          <v-btn color="primary" text :loading="procesando" @click="ejecutarAsignacion">
            Confirmar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { apiClient } from '@/admin/services/API'
import { format, parseISO } from 'date-fns'
import es from 'date-fns/locale/es'
import { notificar, notificarErrorResponse } from '@/admin/utils/notificar'

export default {
  name: 'AsignacionMasiva',
  data() {
    return {
      postulaciones: [],
      seleccionadas: [],
      cargando: false,
      procesando: false,
      totalPostulaciones: 0,
      opciones: {},
      filtroMunicipalidad: null,
      filtroRut: null,
      municipalidades: [],
      estadoAsignar: 'beneficiario',
      dialogConfirmar: false,
    }
  },
  computed: {
    headers() {
      return [
        { text: 'RUT', value: 'rut', sortable: false },
        { text: 'Nombre', value: 'nombre_completo', sortable: false },
        { text: 'Sector', value: 'sector', sortable: false },
        { text: 'Municipalidad', value: 'municipalidad', sortable: false },
        { text: 'Fecha validación', value: 'updated_at', sortable: true },
      ]
    },
    estadosAsignar() {
      return [
        { text: 'Beneficiario', value: 'beneficiario' },
        { text: 'Lista de Espera', value: 'lista_espera' },
      ]
    },
  },
  mounted() {
    this.cargarPostulaciones()
    this.cargarMunicipalidades()
  },
  methods: {
    async cargarPostulaciones() {
      this.cargando = true
      try {
        const { page = 1, itemsPerPage = 15 } = this.opciones
        const params = {
          page,
          per_page: itemsPerPage,
          estado: 'validada',
        }
        if (this.filtroMunicipalidad) {
          params.municipalidad_id = this.filtroMunicipalidad
        }
        if (this.filtroRut) {
          params.rut = this.filtroRut
        }

        const { data } = await apiClient.get('/tramites/incentivos-retiro/asignacion/beneficiarios', { params })

        this.postulaciones = data.data
        this.totalPostulaciones = data.total
      } catch (error) {
        notificarErrorResponse(error, 'Error al cargar postulaciones')
        console.error(error)
      } finally {
        this.cargando = false
      }
    },
    async cargarMunicipalidades() {
      try {
        const { data } = await apiClient.get('usuarios/instituciones', { params: { tipo: 2 } })
        this.municipalidades = data.data || []
      } catch (error) {
        console.error('Error al cargar municipalidades:', error)
      }
    },
    async ejecutarAsignacion() {
      this.procesando = true
      try {
        const ids = this.seleccionadas.map((p) => p.id)
        const { data } = await apiClient.post(
          '/tramites/incentivos-retiro/asignacion/asignacion-masiva',
          {
            postulacion_ids: ids,
            estado: this.estadoAsignar,
          }
        )

        notificar({
          message: data.message || `${data.resultados.exitosos} postulaciones asignadas exitosamente`,
          severity: 'success',
        })

        if (data.resultados.errores && data.resultados.errores.length > 0) {
          notificar({
            message: `${data.resultados.errores.length} error(es) en la asignación`,
            severity: 'warning',
          })
        }

        this.dialogConfirmar = false
        this.seleccionadas = []
        await this.cargarPostulaciones()
      } catch (error) {
        notificarErrorResponse(error, 'Error al ejecutar asignación masiva')
        console.error(error)
      } finally {
        this.procesando = false
      }
    },
    getNombreCompleto(item) {
      return [item.nombres, item.apellido_paterno, item.apellido_materno].filter(Boolean).join(' ')
    },
    formatearFecha(fecha) {
      if (!fecha) return '-'
      try {
        return format(parseISO(fecha), 'dd/MM/yyyy HH:mm', { locale: es })
      } catch {
        return fecha
      }
    },
  },
}
</script>
